import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

/**
 * A hook for managing server-side pagination state.
 * Handles both navigation between loaded pages and fetching new pages.
 */
export const useServerPagination = <T, R>(
  data: T[],
  fetchNextPage: () => void,
  options: {
    dataSelector: (item: T) => R[];
    resetDependencies: unknown[];
    externalIsFetching?: boolean;
    serverHasNextPage?: boolean;
  }
) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const didFetchRef = useRef(false);
  const prevDataLengthRef = useRef(data.length);
  const { resetDependencies, dataSelector, externalIsFetching, serverHasNextPage } = options;

  const loadedPagesCount = data.length;

  // Check if we can navigate to an already-loaded next page
  const hasNextLoadedPage = currentPageIndex < loadedPagesCount - 1;

  // Can go next if there's a loaded page OR server has more to fetch
  const hasNextPage = hasNextLoadedPage || !!serverHasNextPage;

  // Navigate to next loaded page or fetch if at the end of loaded pages
  const goToNextPage = useCallback(() => {
    if (hasNextLoadedPage) {
      // Navigate to already-loaded page
      setCurrentPageIndex((prev) => prev + 1);
    } else if (serverHasNextPage) {
      // Fetch new page from server
      didFetchRef.current = true;
      fetchNextPage();
    }
  }, [hasNextLoadedPage, serverHasNextPage, fetchNextPage]);

  // Advance page index when new data arrives after our fetch
  useEffect(() => {
    if (didFetchRef.current && data.length > prevDataLengthRef.current) {
      setCurrentPageIndex((prev) => prev + 1);
      didFetchRef.current = false;
    }
    prevDataLengthRef.current = data.length;
  }, [data.length]);

  // Reset when dependencies change
  useEffect(() => {
    setCurrentPageIndex(0);
    didFetchRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDependencies);

  // Extract data for the current page
  const currentPageData = useMemo(() => {
    if (!data || data.length === 0 || currentPageIndex >= data.length) {
      return [];
    }
    const pageData = data[currentPageIndex];
    return pageData ? dataSelector(pageData) : [];
  }, [data, currentPageIndex, dataSelector]);

  return {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    currentPageData,
    isFetching: !!externalIsFetching,
  };
};
